
import React, { useEffect, useState } from 'react';
import { useAuth } from '@/contexts/AuthContext';
import { getAvatarUrl } from '@/utils/storage';
import { useProfileManager } from '@/hooks/useProfileManager';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { supabase } from '@/integrations/supabase/client';
import { useToast } from '@/hooks/use-toast';
import { AlertTriangle, Loader2 } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from '@/components/ui/alert-dialog';
import AvatarUpload from './profile/AvatarUpload';
import ProfileFormContainer from './profile/ProfileFormContainer';
import PersonalInfoForm from './profile/PersonalInfoForm';
import PreferencesForm from './profile/PreferencesForm';

interface ClientProfileProps {
  profile: any;
  setProfile: React.Dispatch<React.SetStateAction<any>>;
}

const ClientProfile: React.FC<ClientProfileProps> = ({ profile, setProfile }) => {
  const { user, signOut } = useAuth();
  const { t } = useTranslation('dashboard');
  const navigate = useNavigate();
  const { toast } = useToast();
  const {
    error,
    isSaving,
    avatarFile,
    avatarPreview,
    
    handleAvatarChange,
    updateProfile
  } = useProfileManager(user?.id || '', profile);

  // Initialize avatar URL from profile
  const avatarUrl = profile?.avatar_url ? getAvatarUrl('client.photo', profile.avatar_url) : null;
  const [selectedTimezone, setSelectedTimezone] = useState<string>(profile?.timezone || '');
  const [selectedPreferredLanguage, setSelectedPreferredLanguage] = useState<string>(profile?.preferred_language || 'en');
  const [confirmText, setConfirmText] = useState('');
  const [isDeletingAccount, setIsDeletingAccount] = useState(false);

  useEffect(() => {
    setSelectedTimezone(profile?.timezone || '');
    setSelectedPreferredLanguage(profile?.preferred_language || 'en');
  }, [profile?.timezone, profile?.preferred_language]);

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    
    // Extract form data
    const formData = new FormData(e.currentTarget);
    const values = {
      first_name: formData.get('first_name') as string,
      last_name: formData.get('last_name') as string,
      phone_number: formData.get('phone_number') as string,
      timezone: selectedTimezone?.trim() ? selectedTimezone : null,
      preferred_language: selectedPreferredLanguage || 'en',
      avatar_url: profile?.avatar_url
    };
    
    const success = await updateProfile(values);
    if (success) {
      // Update parent state to reflect changes
      setProfile((prev: any) => ({
        ...prev,
        ...values,
        timezone: values.timezone ?? prev?.timezone ?? null
      }));
    }
  };

  const handleDeleteAccount = async () => {
    if (!user?.id) return;

    if (confirmText.trim().toUpperCase() !== 'DELETE') {
      toast({
        variant: 'destructive',
        title: 'Confirmation required',
        description: 'Please type DELETE to confirm account deletion.',
      });
      return;
    }

    setIsDeletingAccount(true);
    try {
      const { error } = await supabase.functions.invoke('delete-own-account', {
        body: { confirmation: 'DELETE' },
      });

      if (error) {
        throw error;
      }

      toast({
        title: 'Account deleted',
        description: 'Your account and related data have been deleted.',
      });

      await signOut();
      navigate('/client-auth');
    } catch (deleteError: any) {
      toast({
        variant: 'destructive',
        title: 'Delete failed',
        description: deleteError?.message || 'Unable to delete account right now.',
      });
    } finally {
      setIsDeletingAccount(false);
      setConfirmText('');
    }
  };

  return (
    <ProfileFormContainer 
      title={t('clientProfile.title')}
      error={error}
      isSubmitting={isSaving}
      hasAvatarFile={!!avatarFile}
      onSubmit={handleSubmit}
      footerContent={
        <div className="mt-8 border-t border-red-200 pt-6">
          <h3 className="text-lg font-semibold text-red-700 flex items-center gap-2">
            <AlertTriangle className="h-5 w-5" />
            Danger Zone
          </h3>
          <p className="mt-2 text-sm text-gray-600">
            Deleting your account is permanent and will remove your profile and related data.
          </p>

          <AlertDialog>
            <AlertDialogTrigger asChild>
              <Button
                type="button"
                variant="destructive"
                className="mt-4"
                disabled={isDeletingAccount}
              >
                Delete Account
              </Button>
            </AlertDialogTrigger>
            <AlertDialogContent>
              <AlertDialogHeader>
                <AlertDialogTitle>Delete account permanently?</AlertDialogTitle>
                <AlertDialogDescription>
                  This action cannot be undone. Type DELETE to confirm account deletion.
                </AlertDialogDescription>
              </AlertDialogHeader>
              <Input
                value={confirmText}
                onChange={(e) => setConfirmText(e.target.value)}
                placeholder="Type DELETE to confirm"
                disabled={isDeletingAccount}
              />
              <AlertDialogFooter>
                <AlertDialogCancel disabled={isDeletingAccount}>Cancel</AlertDialogCancel>
                <AlertDialogAction
                  onClick={(e) => {
                    e.preventDefault();
                    void handleDeleteAccount();
                  }}
                  className="bg-red-600 hover:bg-red-700 text-white"
                  disabled={isDeletingAccount}
                >
                  {isDeletingAccount ? (
                    <>
                      <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                      Deleting...
                    </>
                  ) : (
                    'Delete Account'
                  )}
                </AlertDialogAction>
              </AlertDialogFooter>
            </AlertDialogContent>
          </AlertDialog>
        </div>
      }
    >
      <div className="flex flex-col sm:flex-row items-center gap-6 mb-8">
        <AvatarUpload 
          avatarUrl={avatarUrl}
          avatarPreview={avatarPreview}
          userId={user?.id || ''} 
          onAvatarChange={handleAvatarChange}
          isUploading={isSaving}
        />
        
        {avatarFile && (
          <div className="mt-2">
            <span className="text-sm text-gray-600">{t('clientProfile.newAvatarSelected')}</span>
            <p className="text-xs text-gray-500">{t('clientProfile.willBeUploaded')}</p>
          </div>
        )}
      </div>
      
      <div className="space-y-6">
        <PersonalInfoForm
          defaultValues={{
            first_name: profile?.first_name || "",
            last_name: profile?.last_name || "",
            email: user?.email || "",
            phone_number: profile?.phone_number || ""
          }}
          onSubmit={() => {}}
          isSubmitting={isSaving}
        />
        
        <div className="mt-8 pt-6 border-t border-gray-200">
          <h3 className="text-lg font-medium mb-4">{t('clientProfile.preferences')}</h3>
          
          <PreferencesForm
            timezone={selectedTimezone}
            preferredLanguage={selectedPreferredLanguage}
            onTimezoneChange={setSelectedTimezone}
            onPreferredLanguageChange={setSelectedPreferredLanguage}
            isSubmitting={isSaving}
          />
        </div>
      </div>
    </ProfileFormContainer>
  );
};

export default ClientProfile;
